import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import MolGrid from "../../../../components/molGrid/MolGrid";
import SwalAlert from "../../../../services/swalService/SwalService";
import { clientValuationListData, clientValuationGridConfig } from "./clientValuationList.grid.config";
import { useDeleteClientValuationMutation, useGetClientValuationListByIdMutation } from "../../../../app/services/clientValuationAPI";
import { useLazyGetAllXmlTemplateListQuery } from "../../../../app/services/xmlReportAPI";
import { addEditXMLReportsFormConfig } from "../../../xMLReports/components/addEditXMLReports/addEditXMLReport.formfields.config";
import { mapDropdownOptions } from "../../../../components/forms/libs/data/mapDropdownOptions";
import { useLazyGetAllReportTemplateListQuery } from "../../../../app/services/reportTemplateAPI";
import { decryptUrlData } from "../../../../services/CryptoService";
import { useParams } from "react-router-dom";
import { forwardRef } from "react";
import ToastService from "../../../../services/toastService/ToastService";
import moment from "moment";
import PropTypes from "prop-types";

const ClientValuationsList = forwardRef((props, ref) => {

  const molGridRef = useRef(null);
  const [dataSource, setDataSource] = useState([]);
  const { confirm } = SwalAlert();
  const [valuationForm, setValuationListForm] = useState(
    clientValuationGridConfig);
  const [sortState, setSortState] = useState([]);

  const [getClientValuationListById, {
    isLoading: isGetClientValuationLoading,
    isSuccess: isGetClientValuationSuccess,
    data: clientValuationData
  }] = useGetClientValuationListByIdMutation();

  const [deleteClientValuation, {
    isLoading: isClientValuationDeleting,
    isSuccess: isClientValuationDeleted,
    data: ClientValuationDeletedData
  }] = useDeleteClientValuationMutation();

  const getClientValuation = (selectRow) => {
    const requestData = {
      planID: selectRow,
    };
    getClientValuationListById(requestData);
  }

  const sortData = (data, sortState) => {
    return [...data].sort((a, b) => {
      for (const sort of sortState) {
        const valA = a[sort.column] ?? '';
        const valB = b[sort.column] ?? '';
        // Determine if values are numbers
        const isNumeric = !isNaN(parseFloat(valA)) && !isNaN(parseFloat(valB));
        let comparison;
        if (isNumeric) {
          comparison = sort.direction === 'asc'
            ? parseFloat(valA) - parseFloat(valB)
            : parseFloat(valB) - parseFloat(valA);
        } else {
          comparison = sort.direction === 'asc'
            ? valA.toString().localeCompare(valB.toString())
            : valB.toString().localeCompare(valA.toString());
        }
        if (comparison !== 0) return comparison;
      }
      return 0;
    });
  };

  const handleSortingChange = async (sortString, col) => {
    if (!sortString) return;
    const newSortState = sortString
      .split(',')
      .map(item => {
        const [column, directionRaw] = item.trim().split(/\s+/);
        const direction = directionRaw?.toLowerCase() === 'desc' ? 'desc' : 'asc';
        return { column, direction };
      })
      .filter(item => item.column);
    const filtered = newSortState.filter(item => item.column === col.fieldName);
    setSortState(newSortState);
    const orderData = await sortData(dataSource, filtered);
    setDataSource(orderData);
  };


  const handleViewClick = (data) => {
    props.onView?.(data);
  };

  const handleDeleteClick = (data) => {
    const valID = data.valID;
    confirm(
      "Delete?",
      "Are you sure you want to delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        if (valID) {
          deleteClientValuation(valID)
        }
      }
    });
  };

  useEffect(() => {
    if (props.selectRow) {
      getClientValuation(props.selectRow); 
    }
  }, [props.selectRow]);

  useEffect(() => {
    if (isGetClientValuationSuccess && clientValuationData) {
      if (clientValuationData) {
        setDataSource(clientValuationData);
      }
    }
  }, [isGetClientValuationSuccess, clientValuationData]);

  useEffect(() => {
    if (isClientValuationDeleted && ClientValuationDeletedData) {
      if (ClientValuationDeletedData.errorMessage) {
        ToastService.success(ClientValuationDeletedData.errorMessage)
      }
      getClientValuation(props.selectRow);
    }
  }, [isClientValuationDeleted, ClientValuationDeletedData]);

  const actionHandler = {
    VIEW: handleViewClick,
    DELETE: handleDeleteClick,
  };

  useImperativeHandle(
    ref,
    () => ({
      refreshGrid: () => {
        if (props.selectRow) {
          getClientValuation(props.selectRow);
        }
      },
    }),
    [props.selectRow]
  );

  return (
    <>
      <MolGrid
        ref={molGridRef}
        configuration={valuationForm}
        dataSource={dataSource}
        allowPagination={false}
        pagination={{
          totalCount: dataSource.length,
          pageSize: 20,
          currentPage: 1,
        }}
        onActionChange={actionHandler}
        isLoading={isGetClientValuationLoading}
        rowDoubleClick={handleViewClick}
        onSorting={handleSortingChange}
      />
    </>
  );
});

ClientValuationsList.propTypes = {
  selectRow: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onView: PropTypes.func,
};

export default ClientValuationsList;
